/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaRecordDetailFields.tsx
 * FILE_ID: JOBHUB-QA-RECORD-DETAIL-FIELDS-001
 * PURPOSE: Job title, quote, client, report title, include flags, and participants.
 * WHY: Settings and the job-row Details column edit the same fields.
 */
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import ComboField from '@quotech/quote-builder/components/ComboField'
import type { QuoteRow } from '../../jobDetailTypes'
import {
  siteLabel,
  type QuoteClientHit,
  type QuoteSiteHit,
} from './useJobQuoteDetails'
import { useInspectionRecordSettings } from './useInspectionRecordSettings'
import { JobQaParticipants } from './JobQaParticipants'
import { QaItpIncludeFields } from './QaItpIncludeFields'

export const INPUT = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm'

function clientLabel(hit: QuoteClientHit): string {
  return (hit.client_name || hit.company_name || '').trim()
}

type RecordDetails = ReturnType<typeof useInspectionRecordSettings>

export function QaRecordDetailFields({
  details,
  jobId,
  quotes,
  sourceQuoteSheetIds,
}: {
  details: RecordDetails
  jobId: number
  quotes: readonly QuoteRow[]
  sourceQuoteSheetIds: readonly number[]
}) {
  const quoteChoices = sourceQuoteSheetIds.length
    ? quotes.filter((row) => sourceQuoteSheetIds.includes(row.id))
    : []
  const onEnter = (event: ReactKeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    commit()
  }
  return (
    <>
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Job title</span>
        <input
          className={INPUT}
          value={details.jobTitle}
          placeholder="Job title"
          onChange={(event) => details.setJobTitle(event.target.value)}
          onBlur={details.commitJobTitle}
          onKeyDown={(event) => onEnter(event, details.commitJobTitle)}
        />
      </label>
      {quoteChoices.length > 0 ? (
        <ComboField<QuoteRow>
          label="Quote ID"
          placeholder="Quote ID"
          value={details.quoteReference}
          inputClassName={INPUT}
          className="min-w-0"
          minChars={0}
          onCommitText={(value) => {
            details.setQuoteReference(value)
            void details.commitQuoteReference()
          }}
          search={async (query) => quoteChoices.filter((row) =>
            (row.quote_number || String(row.id)).toLowerCase().includes(query.trim().toLowerCase()))}
          getKey={(row) => row.id}
          getDisplay={(row) => row.quote_number || String(row.id)}
          renderItem={(row) => row.quote_number || String(row.id)}
          onSelect={(row) => {
            details.setQuoteReference(row.quote_number || String(row.id))
            void details.commitQuoteReference()
          }}
          portal
        />
      ) : (
        <label className="block min-w-0 text-sm">
          <span className="mb-1 block">Quote ID</span>
          <input
            className={INPUT}
            aria-label="Quote ID"
            value={details.quoteReference}
            onChange={(event) => details.setQuoteReference(event.target.value)}
            onBlur={() => { void details.commitQuoteReference() }}
            onKeyDown={(event) => onEnter(event, () => { void details.commitQuoteReference() })}
          />
        </label>
      )}
      <ComboField<QuoteClientHit>
        key={`client-${details.clientReset}`}
        label="Client"
        placeholder="Search clients…"
        revertUnmatchedOnBlur
        value={details.clientName}
        inputClassName={INPUT}
        className="min-w-0"
        onCommitText={() => undefined}
        search={details.searchClients}
        getKey={(hit) => hit.id}
        getDisplay={clientLabel}
        renderItem={(hit) => clientLabel(hit)}
        onSelect={(hit) => { void details.selectClient(hit) }}
        onCreate={details.createClient}
        portal
      />
      <ComboField<QuoteSiteHit>
        key={`site-${details.siteReset}`}
        label="Client location"
        placeholder="Search locations…"
        minChars={1}
        revertUnmatchedOnBlur
        value={details.siteName}
        inputClassName={INPUT}
        className="min-w-0"
        onCommitText={() => undefined}
        search={details.searchSites}
        getKey={(hit) => hit.id}
        getDisplay={siteLabel}
        renderItem={(hit) => siteLabel(hit)}
        onSelect={(hit) => { void details.selectSite(hit) }}
        portal
        onCreate={details.createSite}
      />
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Report title</span>
        <input
          className={INPUT}
          value={details.reportTitle}
          onChange={(event) => details.setReportTitle(event.target.value)}
          onBlur={details.commitReportTitle}
          onKeyDown={(event) => onEnter(event, details.commitReportTitle)}
        />
      </label>
      <QaItpIncludeFields value={details.include} onChange={(key, next) => { void details.setInclude(key, next) }} />
      <JobQaParticipants
        jobId={jobId}
        onError={details.setError}
        people={details.participants}
        onChangePeople={details.saveParticipants}
      />
      {details.error ? (
        <p role="alert" className="text-xs text-destructive">{details.error}</p>
      ) : null}
    </>
  )
}
